export const runtimeEnvironments = ["development", "staging", "production"];
export const localEmulatorFirebaseProjectId = "demo-shdeed-dev";
export const approvedProductionSesSender = "notifications@updates.shdeed.com";
export const approvedCloudSeedFirebaseProjects = {
    development: "shdeed-dev",
    staging: "shdeed-staging",
};
const runtimeEnvironmentSet = new Set(runtimeEnvironments);
export class EnvironmentConfigurationError extends Error {
    constructor(message) {
        super(message);
        this.name = "EnvironmentConfigurationError";
    }
}
export function parseRuntimeEnvironment(value, variableName = "APP_ENVIRONMENT") {
    if (value === undefined || value.trim() === "") {
        throw new EnvironmentConfigurationError(`${variableName} is required and must be one of: ${runtimeEnvironments.join(", ")}.`);
    }
    const normalized = value.trim().toLowerCase();
    if (runtimeEnvironmentSet.has(normalized)) {
        return normalized;
    }
    throw new EnvironmentConfigurationError(`${variableName} must be one of: ${runtimeEnvironments.join(", ")}.`);
}
export function parseBooleanFlag(value, variableName) {
    if (typeof value === "boolean") {
        return value;
    }
    if (value === undefined || value.trim() === "") {
        throw new EnvironmentConfigurationError(`${variableName} is required.`);
    }
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") {
        return true;
    }
    if (normalized === "false") {
        return false;
    }
    throw new EnvironmentConfigurationError(`${variableName} must be true or false.`);
}
export function environmentBannerLabel(environment) {
    if (environment === "development") {
        return "DEVELOPMENT";
    }
    if (environment === "staging") {
        return "STAGING";
    }
    return undefined;
}
export function requireEnvironmentValue(value, variableName) {
    if (value === undefined || value.trim() === "") {
        throw new EnvironmentConfigurationError(`${variableName} is required.`);
    }
    return value.trim();
}
export function parseAppCheckEnforcementMode(value) {
    const normalized = requireEnvironmentValue(value, "APP_CHECK_ENFORCEMENT_MODE").toLowerCase();
    if (normalized === "disabled" || normalized === "observe" || normalized === "enforce") {
        return normalized;
    }
    throw new EnvironmentConfigurationError("APP_CHECK_ENFORCEMENT_MODE must be disabled, observe, or enforce.");
}
export function assertAppCheckEnvironment(input) {
    if (input.environment === "production" && input.mode === "disabled") {
        throw new EnvironmentConfigurationError("Production App Check must be set to observe or enforce.");
    }
    if (input.environment === "development" && input.mode === "enforce") {
        throw new EnvironmentConfigurationError("Development App Check must not enforce tokens because local emulator workflows do not issue production App Check tokens.");
    }
}
export function appCheckFunctionsEnforced(mode) {
    return mode === "enforce";
}
export function assertNoPlaceholderConfigurationValue(value, variableName) {
    if (looksLikePlaceholder(value)) {
        throw new EnvironmentConfigurationError(`${variableName} must not be a placeholder.`);
    }
}
export function looksLikeProductionFirebaseProject(projectId) {
    return /(^|[-_])(prod|production)([-_]|$)/i.test(projectId);
}
function looksLikeDevelopmentFirebaseProject(projectId) {
    return /(^|[-_])(dev|development)([-_]|$)/i.test(projectId);
}
function looksLikeStagingFirebaseProject(projectId) {
    return /(^|[-_])(staging|stage)([-_]|$)/i.test(projectId);
}
function looksLikePlaceholder(value) {
    return /placeholder|example|todo|replace|demo-api-key|000000000000/i.test(value);
}
function looksLikeDemoProject(projectId) {
    return projectId === localEmulatorFirebaseProjectId || /^demo[-_]/i.test(projectId);
}
export function assertFirebaseEnvironment(input) {
    const variableName = input.variableName ?? "FIREBASE_PROJECT_ID";
    const projectId = requireEnvironmentValue(input.projectId, variableName);
    if (input.useEmulators && input.environment !== "development") {
        throw new EnvironmentConfigurationError("Firebase emulators are allowed only in the development environment.");
    }
    if (input.useEmulators && projectId !== localEmulatorFirebaseProjectId) {
        throw new EnvironmentConfigurationError(`Firebase emulator mode must use ${localEmulatorFirebaseProjectId}.`);
    }
    if (!input.useEmulators && looksLikeDemoProject(projectId)) {
        throw new EnvironmentConfigurationError(`${variableName} must not use a demo project when emulator mode is disabled.`);
    }
    if (input.environment !== "production" && looksLikeProductionFirebaseProject(projectId)) {
        throw new EnvironmentConfigurationError(`${input.environment} must not target a production-like Firebase project.`);
    }
    if (input.environment === "staging" && looksLikePlaceholder(projectId)) {
        throw new EnvironmentConfigurationError("Staging Firebase project ID must be explicit.");
    }
    if (input.environment === "production" &&
        (looksLikeDemoProject(projectId) ||
            looksLikeDevelopmentFirebaseProject(projectId) ||
            looksLikeStagingFirebaseProject(projectId) ||
            looksLikePlaceholder(projectId))) {
        throw new EnvironmentConfigurationError("Production Firebase project ID must be explicit.");
    }
}
export function assertSyntheticCloudSeedTarget(input) {
    if (input.environment === "production") {
        throw new EnvironmentConfigurationError("Synthetic cloud seed must never target production.");
    }
    const expectedProjectId = approvedCloudSeedFirebaseProjects[input.environment];
    const projectId = requireEnvironmentValue(input.projectId, "FIREBASE_PROJECT_ID");
    if (projectId !== expectedProjectId) {
        throw new EnvironmentConfigurationError(`${input.environment} cloud seed must target ${expectedProjectId}.`);
    }
    const confirmationProjectId = requireEnvironmentValue(input.confirmationProjectId, "SHDEED_ALLOW_CLOUD_SEED_PROJECT_ID");
    if (confirmationProjectId !== projectId) {
        throw new EnvironmentConfigurationError("SHDEED_ALLOW_CLOUD_SEED_PROJECT_ID must exactly match FIREBASE_PROJECT_ID.");
    }
}
export function assertRuntimeFirebaseProject(input) {
    if (input.runtimeProjectId === undefined || input.runtimeProjectId.trim() === "") {
        return;
    }
    const runtimeProjectId = input.runtimeProjectId.trim();
    if (runtimeProjectId !== input.configuredProjectId) {
        throw new EnvironmentConfigurationError(`Runtime Firebase project ${runtimeProjectId} does not match configured project ${input.configuredProjectId}.`);
    }
    if (input.environment !== "production" && looksLikeProductionFirebaseProject(runtimeProjectId)) {
        throw new EnvironmentConfigurationError(`${input.environment} runtime must not execute inside a production Firebase project.`);
    }
}
export function parseStripeMode(value) {
    const normalized = requireEnvironmentValue(value, "STRIPE_MODE").toLowerCase();
    if (normalized === "disabled" || normalized === "test" || normalized === "live") {
        return normalized;
    }
    throw new EnvironmentConfigurationError("STRIPE_MODE must be disabled, test, or live.");
}
export function assertStripeEnvironment(input) {
    if (input.mode === "disabled") {
        return;
    }
    if (input.environment === "production" && input.mode !== "live") {
        throw new EnvironmentConfigurationError("Production Stripe mode must be live.");
    }
    if (input.environment !== "production" && input.mode === "live") {
        throw new EnvironmentConfigurationError("Development and staging must use Stripe test mode.");
    }
}
export function assertStripeCredentialEnvironment(input) {
    const secretKey = input.secretKey?.trim() ?? "";
    const webhookSecret = input.webhookSecret?.trim() ?? "";
    if (input.mode === "disabled") {
        if (secretKey !== "" || webhookSecret !== "") {
            throw new EnvironmentConfigurationError("Disabled Stripe mode must not load payment or webhook credentials.");
        }
        return;
    }
    if (input.environment === "production") {
        if (secretKey === "") {
            throw new EnvironmentConfigurationError("Production Stripe secret key must be configured in the protected environment.");
        }
        if (webhookSecret === "") {
            throw new EnvironmentConfigurationError("Production Stripe webhook secret must be configured in the protected environment.");
        }
        if (secretKey.startsWith("sk_test_")) {
            throw new EnvironmentConfigurationError("Production Stripe configuration must not use test secret keys.");
        }
        return;
    }
    if (secretKey.startsWith("sk_live_")) {
        throw new EnvironmentConfigurationError("Development and staging must not load Stripe live secret keys.");
    }
    if (input.mode !== "test") {
        throw new EnvironmentConfigurationError("Development and staging Stripe credentials require test mode.");
    }
}
export function parseSesDeliveryMode(value) {
    const normalized = requireEnvironmentValue(value, "SES_DELIVERY_MODE").toLowerCase();
    if (normalized === "local_capture" ||
        normalized === "ses_sandbox" ||
        normalized === "production") {
        return normalized;
    }
    throw new EnvironmentConfigurationError("SES_DELIVERY_MODE must be local_capture, ses_sandbox, or production.");
}
export function assertSesEnvironment(input) {
    if (input.environment === "production") {
        if (input.mode !== "production") {
            throw new EnvironmentConfigurationError("Production SES mode must be production.");
        }
        if (input.senderAddress !== approvedProductionSesSender) {
            throw new EnvironmentConfigurationError(`Production SES sender must be ${approvedProductionSesSender}.`);
        }
        return;
    }
    if (input.mode === "production") {
        throw new EnvironmentConfigurationError("Development and staging must not use unrestricted production SES delivery.");
    }
}
export function parseFcmMode(value) {
    const normalized = requireEnvironmentValue(value, "FCM_MODE").toLowerCase();
    if (normalized === "disabled" || normalized === "test" || normalized === "production") {
        return normalized;
    }
    throw new EnvironmentConfigurationError("FCM_MODE must be disabled, test, or production.");
}
export function assertFcmEnvironment(input) {
    const tokenNamespace = requireEnvironmentValue(input.tokenNamespace, "FCM_TOKEN_NAMESPACE");
    if (!tokenNamespace.startsWith(`${input.environment}:`)) {
        throw new EnvironmentConfigurationError("FCM_TOKEN_NAMESPACE must be prefixed with the runtime environment.");
    }
    if (input.environment === "production" && input.mode !== "production") {
        throw new EnvironmentConfigurationError("Production FCM mode must be production.");
    }
    if (input.environment !== "production" && input.mode === "production") {
        throw new EnvironmentConfigurationError("Development and staging must not use production FCM.");
    }
}
export function parseAiProviderMode(value) {
    const normalized = requireEnvironmentValue(value, "AI_PROVIDER").toLowerCase();
    if (normalized === "disabled" ||
        normalized === "mock" ||
        normalized === "test" ||
        normalized === "cloud") {
        return normalized;
    }
    throw new EnvironmentConfigurationError("AI_PROVIDER must be disabled, mock, test, or cloud.");
}
export function assertAiEnvironment(input) {
    if (input.environment !== "production" && input.provider === "cloud") {
        throw new EnvironmentConfigurationError("Development and staging must not require entitled cloud AI.");
    }
}
